import { formatDate } from '@angular/common';
import { Component, computed, inject, OnInit, signal, viewChild } from '@angular/core';
import { ColDef, GridOptions } from 'ag-grid-community';
import { finalize } from 'rxjs';
import { PanModalComponent } from '../../../components/pan-modal/pan-modal.component';
import { tryGetErrorMessage } from '../../../error.utils';
import { defaultTableDefOptions } from '../../../helpers';
import { NotifyService } from '../../../service/notify.service';
import { ReportsService } from '../../../service/reports.service';
import { UserLoginService } from '../../../service/user-login.service';
import { ReportCategory, ReportResponse, ReportSharedResponse } from '../../../types/reports';
import { CellReportDashboardActionsComponent } from './cell-report-dashboard-actions/cell-report-dashboard-actions.component';

@Component({
  selector: 'app-report-dashboard',
  templateUrl: './report-dashboard.component.html',
  styleUrls: ['./report-dashboard.component.scss'],
  standalone: false,
  providers: [],
})
export class ReportDashboardComponent implements OnInit {
  public userService = inject(UserLoginService);
  private reportService = inject(ReportsService);
  private notifyService = inject(NotifyService);

  public isLoading = signal<boolean>(false);
  public allReports = signal<ReportResponse[]>([]);
  public currentView = signal<ReportCategory>('Mine');
  public reportHistoryChosenName = signal<string>('');
  public reportHistoryLoading = signal<boolean>(false);
  public reportHistory = signal<ReportSharedResponse[]>([]);

  public reportHistoryModal = viewChild<PanModalComponent>('viewHistoryModal');

  public myReportsCount = computed(() => this.allReports().filter((report) => report.category === 'Mine').length);
  public sharedReportsCount = computed(() => this.allReports().filter((report) => report.category === 'Shared').length);

  public filteredReports = computed(() => {
    return this.allReports().filter((report) => report.category === this.currentView());
  });

  public readonly tableDef: GridOptions<ReportResponse> = {
    ...defaultTableDefOptions(),
    pagination: true,
    paginationPageSize: 10,
    paginationPageSizeSelector: [10, 20, 50],
  };

  public readonly columnDefs: ColDef<ReportResponse>[] = [
    {
      field: 'title',
      headerName: 'Report Name',
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
    },
    {
      field: 'locations',
      headerName: 'Locations',
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
      valueGetter: (params) =>
        params.data.locations
          .map((l) => l.name)
          .sort()
          .join(', '),
      tooltipValueGetter: (params) => params.value,
    },
    {
      field: 'case_count',
      headerName: 'Total Cases',
      cellDataType: 'number',
      filter: 'agNumberColumnFilter',
    },
    {
      field: 'created_by',
      headerName: 'Shared By',
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
    },
    {
      field: 'created_at',
      headerName: 'Shared At',
      cellDataType: 'date',
      filter: 'agDateColumnFilter',
      valueFormatter: (params) => formatDate(params.value, 'medium', 'en-US'),
    },
    {
      headerName: '',
      suppressKeyboardEvent: () => true,
      cellRenderer: CellReportDashboardActionsComponent,
      cellRendererParams: {
        historyCallback: (element: ReportResponse) => {
          this.reportHistoryChosenName.set(element.title);
          this.reportHistoryLoading.set(true);
          this.reportService
            .getReportShareHistory(element.id)
            .pipe(finalize(() => this.reportHistoryLoading.set(false)))
            .subscribe((response) => {
              this.reportHistoryModal().show();
              this.reportHistory.set(response.items.reverse());
            });
        },
        removeCallback: (element: ReportResponse) => {
          this.deleteReport(element);
        },
      },
      sortable: false,
    },
  ];

  ngOnInit(): void {
    this.isLoading.set(true);

    this.reportService
      .getUserReports()
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe((response) => {
        this.allReports.set(response.items);
        this.setDefaultView();
      });
  }

  setDefaultView() {
    if (this.userService.currentUserIsGuest()) {
      this.currentView.set('Shared');
    } else {
      this.currentView.set('Mine');
    }
  }

  deleteReport(report: ReportResponse) {
    this.reportService.deleteReportById(report.id).subscribe({
      next: () => this.notifyService.notify('Report deleted successfully'),
      error: (err) => this.notifyService.notify(tryGetErrorMessage(err)),
    });
  }

  clearHistory() {
    this.reportHistoryChosenName.set('');
    this.reportHistory.set([]);
  }
}
